import * as Dialog from '@radix-ui/react-dialog';
import { cn } from '@/utils/cn';

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'default';
  onConfirm: () => void;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = '确定',
  cancelLabel = '取消',
  variant = 'danger',
  onConfirm,
}: ConfirmDialogProps): JSX.Element {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-xl border border-border bg-card p-6 shadow-2xl data-[state=open]:animate-in data-[state=closed]:animate-out">
          <Dialog.Title className="text-base font-semibold text-foreground">{title}</Dialog.Title>
          <Dialog.Description className="mt-2 text-sm text-muted-foreground">{description}</Dialog.Description>
          <div className="mt-5 flex justify-end gap-2">
            <Dialog.Close asChild>
              <button className="rounded-lg border border-border px-4 py-2 text-sm text-muted-foreground hover:bg-muted">
                {cancelLabel}
              </button>
            </Dialog.Close>
            <button
              className={cn(
                'rounded-lg px-4 py-2 text-sm font-medium text-white',
                variant === 'danger'
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-primary hover:bg-primary/90',
              )}
              onClick={() => {
                onConfirm();
                onOpenChange(false);
              }}
            >
              {confirmLabel}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ===== Hook for easy use =====
import { useState, useCallback } from 'react';

export function useConfirm() {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState<{ title: string; desc: string; onConfirm: () => void; variant?: 'danger' | 'default' }>({
    title: '',
    desc: '',
    onConfirm: () => {},
  });

  const confirm = useCallback((title: string, desc: string, onConfirm: () => void, variant: 'danger' | 'default' = 'danger') => {
    setOpts({ title, desc, onConfirm, variant });
    setOpen(true);
  }, []);

  const dialog = (
    <ConfirmDialog
      open={open}
      onOpenChange={setOpen}
      title={opts.title}
      description={opts.desc}
      variant={opts.variant}
      onConfirm={opts.onConfirm}
    />
  );

  return { confirm, dialog };
}
